import React from 'react';
import './FAQsPage.css';
import { Link } from 'react-router-dom';

const faqs = [
  {
    q: "What do window film/tint acronyms like VLT, IR, TSER, and UV really mean?",
    a: (
      <>
        When diving into the world of window tinting, you'll likely encounter technical terms like <b>VLT</b>, <b>IR</b>, <b>TSER</b>, and <b>UV</b>. These acronyms represent key performance metrics that determine how effective a window film is.<br /><br />
        <b>UV (Ultraviolet):</b> Ultraviolet radiation lies between 10 nm and 400 nm on the electromagnetic spectrum. It's one of the main culprits behind skin aging, eye damage, and the fading of furniture and interiors.<br />
        <b>UVR (Ultraviolet Rejection):</b> This represents the percentage of UV rays blocked by the film. A high UVR (up to 100%) is crucial to protect your skin and interior spaces.<br />
        <b>VLT (Visible Light Transmission):</b> This indicates the percentage of visible light that passes through the window film. Lower VLT means a darker film and more privacy. Higher VLT allows more natural light inside. For example, a film with 5% VLT is very dark, while 50% VLT is much lighter.<br />
        <b>IR (Infrared):</b> Infrared light has longer wavelengths than visible light. Though invisible, it's experienced as heat. Blocking IR radiation helps reduce heat buildup.<br />
        <b>IRR (Infrared Rejection):</b> This tells you how much infrared heat the film can reflect. It's a useful metric, but it doesn't represent total heat rejection.<br />
        <b>TSER (Total Solar Energy Rejected):</b> TSER measures how much total solar energy (UV, visible light, and infrared) is blocked by the film. It gives a full picture of the film's heat rejection capabilities.<br />
        <b>Ply:</b> The number of polyester film layers in the window film. More plies usually mean better durability and performance.<br />
        <b>Mil Thickness:</b> Measured in thousandths of an inch, mil thickness reflects the film's physical thickness. For example, 2mil to 12mil options exist for varying levels of protection.
      </>
    ),
  },
  {
    q: "What's the difference between TSER and IR Rejection?",
    a: (
      <>
        <b>TSER (Total Solar Energy Rejected)</b> is a more holistic metric than IRR. While IRR only measures how much infrared radiation (heat) is rejected, TSER covers all three components of solar energy—infrared, visible light, and ultraviolet rays. This means TSER is a more accurate indicator of overall heat rejection.<br /><br />
        Some brands may advertise high IRR by measuring a small portion of the infrared spectrum. This can be misleading because it doesn't always translate to real-world heat performance. Unless IRR is measured across the full IR spectrum (up to 2,500 nm), TSER remains the more reliable benchmark.
      </>
    ),
  },
  {
    q: "How can I determine a high-performance window film based on TSER, UVR, and VLT?",
    a: (
      <>
        A quality window film will show balanced performance across all three key values:<br />
        <ul>
          <li><b>TSER:</b> A good range is between 70% and 85%. This ensures effective heat reduction.</li>
          <li><b>UVR:</b> Look for 95% to 100% UV rejection to guard against skin damage and interior fading.</li>
          <li><b>VLT:</b> Choose a value based on your privacy and lighting needs, keeping local regulations in mind. Lower VLT values (e.g., 5%) provide more shading and privacy, while higher VLT (e.g., 50%) allows more natural light in.</li>
        </ul>
      </>
    ),
  },
  {
    q: "Why do furnishings fade, and how can window films help?",
    a: (
      <>
        Fading of furniture, flooring, and interiors is mainly caused by:<br />
        <ul>
          <li>Ultraviolet light (45%)</li>
          <li>Visible light (25%)</li>
          <li>Heat (25%)</li>
          <li>Other minor factors (5%)</li>
        </ul>
        Window films help slow down this fading by blocking a significant portion of these causes—especially UV and heat. A high-quality film can dramatically extend the lifespan and appearance of your furnishings.
      </>
    ),
  },
  {
    q: "Is IRR always a good indicator of a film's heat performance?",
    a: (
      <>
        Not necessarily. IRR can be misleading if it's not measured across the full infrared spectrum. Some manufacturers highlight high IRR values based on narrow bands of the IR range, which may not reflect real-world heat rejection. TSER is a more dependable metric as it includes UV, visible light, and infrared—giving a complete picture of the film's performance.
      </>
    ),
  },
  {
    q: "Can window film reduce glare inside my vehicle or home?",
    a: (
      <>
        Yes. Window films with lower VLT values are effective at reducing glare from sunlight or oncoming headlights. This makes driving safer and improves indoor comfort when working or watching screens during the day.
      </>
    ),
  },
  {
    q: "Does window tint affect visibility at night?",
    a: (
      <>
        It can, depending on the VLT of the film. Extremely dark films (5% VLT or lower) may reduce visibility in low-light conditions. For night driving or residential use, a balanced VLT (between 20%–50%) is generally recommended.
      </>
    ),
  },
  {
    q: "Are all high-IRR films high-performance?",
    a: (
      <>
        No. Infrared rejection numbers can be exaggerated. If a film only blocks a narrow part of the IR spectrum but leaves out heat from the broader range, its real-life performance may be underwhelming. Always consider TSER alongside IRR for a clearer picture.
      </>
    ),
  },
  {
    q: "What's the lifespan of window film?",
    a: (
      <>
        It depends on the type, quality, and environmental conditions. Premium films from reputable brands can last anywhere from 5 to 10 years, especially if professionally installed and properly maintained.
      </>
    ),
  },
  {
    q: "Will window films block my phone, GPS, or Wi-Fi signal?",
    a: (
      <>
        Most modern films—including those from Derma Protec—are designed not to interfere with signals. However, metallic-based films may slightly reduce signal strength, so if connectivity is important to you, opt for ceramic or non-metallic alternatives.
      </>
    ),
  },
  {
    q: "What's the difference between dyed, metallic, and ceramic films?",
    a: (
      <>
        <b>Dyed Films:</b> Use dyes to absorb solar heat. They are typically lower-cost and offer basic protection but may fade over time.<br />
        <b>Metallic Films:</b> Contain metal particles that reflect solar energy. They offer excellent heat rejection but can interfere with GPS, mobile, and radio signals.<br />
        <b>Ceramic Films:</b> Use nano-ceramic particles that offer superior heat and UV rejection without affecting signals. These are the premium choice for clarity, durability, and non-interference with electronics.
      </>
    ),
  },
  {
    q: "Will window films help reduce energy bills?",
    a: (
      <>
        Yes, high-performance films act like insulation for your windows. By rejecting solar heat in the summer and helping to retain warmth in the winter, they reduce the load on your air conditioning and heating systems. Over time, this translates to lower electricity bills and a more comfortable environment year-round.
      </>
    ),
  },
];

const FAQsPage = () => (
  <main className="faqs-main">
    <section className="faqs-hero">
      <h1>Frequently Asked Questions (FAQs)</h1>
    </section>
    <div className="faqs-hero-img-wrapper">
      <img 
        src="https://i.postimg.cc/k4wrz4P9/faqs-derma.png" 
        alt="FAQs Illustration" 
        className="faqs-hero-img"
        style={{ maxWidth: '420px', width: '100%', height: 'auto', margin: '0 auto 32px auto', display: 'block', borderRadius: '16px', boxShadow: '0 2px 12px rgba(53,159,149,0.10)' }}
      />
    </div>
    <section className="faqs-list-section">
      <div className="faqs-list">
        {faqs.map((faq, idx) => (
          <div className="faq-item" key={idx}>
            <h2 className="faq-question">{idx + 1}. {faq.q}</h2>
            <div className="faq-answer">{faq.a}</div>
          </div>
        ))}
      </div>
      <div className="faqs-cta">
        <p>If you still have questions or need guidance on selecting the right film for your needs, our team at Derma Protec Films is here to help.</p>
        <Link to="/contact" className="faqs-contact-btn">Contact Us</Link>
      </div>
    </section>
  </main>
);

export default FAQsPage; 